import type { ReactNode } from 'react';
import { createRoot } from 'react-dom/client';
import './styles.css';

type IconName = 'arrow' | 'check' | 'file' | 'lock' | 'spark' | 'sun' | 'import';

const Arrow = ({ className = '' }: { className?: string }) => (
  <svg className={className} viewBox="0 0 20 20" aria-hidden="true">
    <path d="M4 10h11M10.5 4.5 16 10l-5.5 5.5" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" />
  </svg>
);

function Icon({ name, size = 22 }: { name: IconName; size?: number }) {
  const common = { width: size, height: size, viewBox: '0 0 24 24', fill: 'none', 'aria-hidden': true } as const;
  const stroke = { stroke: 'currentColor', strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const, strokeWidth: 1.65 };

  if (name === 'lock') {
    return <svg {...common}><rect x="5" y="10" width="14" height="10" rx="2" {...stroke} /><path d="M8 10V7a4 4 0 0 1 8 0v3M12 14v2" {...stroke} /></svg>;
  }
  if (name === 'file') {
    return <svg {...common}><path d="M6 3.75h8l4 4V20.25H6z" {...stroke} /><path d="M14 3.75v4h4M9 12h6M9 15.5h6" {...stroke} /></svg>;
  }
  if (name === 'import') {
    return <svg {...common}><path d="M12 4v10M8.25 10.5 12 14.25l3.75-3.75M5 18.75h14" {...stroke} /></svg>;
  }
  if (name === 'sun') {
    return <svg {...common}><circle cx="12" cy="12" r="3.5" {...stroke} /><path d="M12 2.75v2M12 19.25v2M21.25 12h-2M4.75 12h-2M18.54 5.46l-1.42 1.42M6.88 17.12l-1.42 1.42M18.54 18.54l-1.42-1.42M6.88 6.88 5.46 5.46" {...stroke} /></svg>;
  }
  if (name === 'spark') {
    return <svg {...common}><path d="m12 3 1.4 5.6L19 10l-5.6 1.4L12 17l-1.4-5.6L5 10l5.6-1.4zM18.5 15.25l.65 2.6 2.6.65-2.6.65-.65 2.6-.65-2.6-2.6-.65 2.6-.65z" {...stroke} /></svg>;
  }
  return <svg {...common}><path d="m5 12.5 4.1 4.1L19 6.75" {...stroke} /></svg>;
}

function Logo({ inverse = false }: { inverse?: boolean }) {
  return (
    <a className={`brand ${inverse ? 'brand-inverse' : ''}`} href="/" aria-label="Risparmio Plus, home">
      <img className="brand-logo" src="/risparmio-plus-mark.svg" alt="" width="31" height="31" />
      <span className="brand-name">Risparmio Plus</span>
    </a>
  );
}

function SiteHeader({ inverse = false }: { inverse?: boolean }) {
  return (
    <header className={`site-header ${inverse ? 'site-header-inverse' : ''}`}>
      <div className="container header-inner">
        <Logo inverse={inverse} />
        <nav className="site-nav" aria-label="Navigazione principale">
          <a href="/#come-funziona">Come funziona</a>
          <a href="/#privacy">Privacy</a>
          <a className="nav-pill" href="/privacy.html">Leggi la privacy <Arrow /></a>
        </nav>
      </div>
    </header>
  );
}

function SectionLabel({ children, inverse = false }: { children: ReactNode; inverse?: boolean }) {
  return <p className={`eyebrow ${inverse ? 'eyebrow-inverse' : ''}`}>{children}</p>;
}

const features = [
  { icon: 'lock' as const, title: 'Prima di tutto, privato', body: 'Niente account e niente cloud obbligatorio. I tuoi movimenti restano nel tuo dispositivo.' },
  { icon: 'import' as const, title: 'Importa in un attimo', body: 'Carica i tuoi estratti conto Intesa Sanpaolo o Revolut e lasciali diventare movimenti ordinati.' },
  { icon: 'spark' as const, title: 'Insight che servono', body: 'Un riferimento semplice per oggi, senza sensi di colpa e senza grafici da decifrare.' },
];

function AppPreview() {
  return (
    <div className="preview-wrap" aria-label="Anteprima della schermata principale di Risparmio Plus">
      <div className="preview-orbit orbit-one" />
      <div className="preview-orbit orbit-two" />
      <div className="preview-phone">
        <div className="preview-topline"><span>RISPARMIO PLUS</span><span className="preview-avatar">A</span></div>
        <p className="preview-greeting">Buongiorno, Alex</p>
        <div className="preview-month">Agosto 2026 <span>⌄</span></div>
        <div className="preview-budget">
          <div className="preview-kicker">PUOI ANCORA SPENDERE</div>
          <div className="preview-amount">292,30 €</div>
          <p>Quello che puoi ancora spendere senza cambiare i tuoi piani.</p>
          <div className="preview-progress"><span /></div>
        </div>
        <div className="preview-daily">
          <span className="preview-sun"><Icon name="sun" size={18} /></span>
          <div><div className="preview-kicker">BUSSOLA DEL GIORNO</div><strong>36,53 € per oggi</strong><p>Un riferimento, non un limite.</p></div>
        </div>
        <div className="preview-footer"><span>Uscite<strong>907,70 €</strong></span><span>Andamento<strong className="preview-positive">Dentro il piano</strong></span></div>
      </div>
      <div className="preview-note"><Icon name="lock" size={16} /><span>Solo sul tuo dispositivo</span></div>
    </div>
  );
}

function LandingPage() {
  return (
    <div className="site-shell">
      <SiteHeader />
      <main>
        <section className="hero container">
          <div className="hero-copy">
            <SectionLabel>Spese, ma con più serenità</SectionLabel>
            <h1>I tuoi soldi.<br /><em>Più chiari.</em></h1>
            <p className="hero-lead">Risparmio Plus ti aiuta a capire cosa puoi spendere oggi, con una vista semplice del mese e la tranquillità che i tuoi dati restano sempre con te.</p>
            <div className="hero-actions">
              <a className="button button-dark" href="#come-funziona">Scopri come funziona <Arrow /></a>
              <a className="text-link" href="/privacy.html">La nostra privacy <Arrow /></a>
            </div>
            <div className="hero-proof"><span className="proof-mark"><Icon name="lock" size={14} /></span><span>Local-first. Nessun account richiesto.</span></div>
          </div>
          <AppPreview />
        </section>

        <section className="statement-section" id="privacy">
          <div className="container statement-grid">
            <SectionLabel inverse>La differenza è dove restano</SectionLabel>
            <div>
              <h2>Niente cloud obbligatorio.<br /><em>Niente rumore.</em></h2>
              <p>Il denaro è personale. Per questo Risparmio Plus nasce local-first: l’app lavora sul tuo dispositivo, senza chiederti di aprire un account o consegnare i tuoi movimenti a un servizio online.</p>
              <a className="button button-light" href="/privacy.html">Leggi la privacy policy <Arrow /></a>
            </div>
          </div>
        </section>

        <section className="features-section" id="come-funziona">
          <div className="container">
            <div className="section-intro"><div><SectionLabel>Un piccolo spazio per pensare meglio</SectionLabel><h2>Il controllo che<br /><em>non pesa.</em></h2></div><p>Una quotidianità più leggibile, costruita intorno alle decisioni reali: una spesa, un importo, un giorno alla volta.</p></div>
            <div className="feature-list">
              {features.map((feature, index) => (
                <article className="feature-item" key={feature.title}>
                  <div className="feature-index">0{index + 1}</div>
                  <div className="feature-icon"><Icon name={feature.icon} /></div>
                  <div className="feature-content"><h3>{feature.title}</h3><p>{feature.body}</p></div>
                  <span className="feature-arrow"><Arrow /></span>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="closing-section container">
          <div className="closing-card">
            <div><SectionLabel>Per cominciare</SectionLabel><h2>Fai spazio<br /><em>alla calma.</em></h2></div>
            <div className="closing-copy"><p>Non serve diventare esperti di finanza personale. Serve solo vedere le cose come sono.</p><a className="button button-dark" href="mailto:it.musme@gmail.com?subject=Risparmio%20Plus">Scrivici <Arrow /></a></div>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}

function PolicySection({ id, eyebrow, title, children }: { id: string; eyebrow: string; title: string; children: ReactNode }) {
  return <section className="policy-section" id={id}><SectionLabel>{eyebrow}</SectionLabel><h2>{title}</h2>{children}</section>;
}

function PrivacyPage() {
  return (
    <div className="site-shell privacy-shell">
      <section className="privacy-hero">
        <SiteHeader inverse />
        <div className="container privacy-hero-copy"><SectionLabel inverse>Risparmio Plus / Trasparenza</SectionLabel><h1>La privacy,<br /><em>senza asterischi.</em></h1><p>Una spiegazione chiara di cosa succede ai tuoi dati quando usi Risparmio Plus.</p><div className="policy-date">Ultimo aggiornamento <strong>25 agosto 2026</strong></div></div>
      </section>
      <main className="policy-layout container">
        <aside className="policy-aside"><div className="aside-sticky"><span className="aside-label">In questa pagina</span><a href="#principi">In breve</a><a href="#dati">Dati nell’app</a><a href="#importazioni">Importazioni</a><a href="#sicurezza">Sicurezza</a><a href="#diritti">Controllo e cancellazione</a><a href="#contatti">Contatti</a></div></aside>
        <article className="policy-copy">
          <p className="policy-lead">Risparmio Plus è progettata per funzionare prima di tutto sul tuo dispositivo. Non richiede un account e non invia automaticamente i tuoi movimenti, budget o categorie a un server.</p>
          <PolicySection id="principi" eyebrow="01 / In breve" title="I tuoi dati restano con te.">
            <p>L’app è local-first: il database e le impostazioni vengono salvati nella sandbox locale dell’app. Non utilizziamo pubblicità, analytics o SDK di tracciamento nel client mobile attuale.</p>
            <div className="policy-callout"><Icon name="lock" size={20} /><div><strong>Nessun account. Nessun cloud obbligatorio.</strong><span>Puoi usare Risparmio Plus anche senza registrarti e senza collegare direttamente il tuo conto bancario.</span></div></div>
          </PolicySection>
          <PolicySection id="dati" eyebrow="02 / Cosa gestisce l’app" title="Solo ciò che serve per funzionare.">
            <p>Puoi inserire o importare movimenti, categorie, budget, ricorrenze e il nome del profilo. Questi contenuti vengono usati per mostrarti il riepilogo del mese, gli insight e i promemoria che hai scelto.</p>
            <p>Quando disponibile, nelle build native di release il database è cifrato con SQLCipher. La chiave viene conservata nel Keychain iOS o nel Keystore Android tramite SecureStore e non è inclusa nel codice dell’app.</p>
          </PolicySection>
          <PolicySection id="importazioni" eyebrow="03 / Importa con calma" title="Gli estratti conto vengono letti localmente.">
            <p>La funzione di importazione può leggere file CSV o XLSX esportati, ad esempio, da Intesa Sanpaolo o Revolut. L’app interpreta il file sul dispositivo, propone i movimenti riconosciuti e li salva solo dopo la tua conferma.</p>
            <p>Non chiediamo le credenziali della banca e non usiamo un collegamento diretto al conto. Il file che scegli dal dispositivo viene consegnato all’app dal sistema operativo per essere elaborato localmente.</p>
          </PolicySection>
          <PolicySection id="sicurezza" eyebrow="04 / Quando scegli tu" title="Condividere è una tua decisione.">
            <p>La funzione “Esporta una copia protetta” crea un file JSON cifrato localmente con XChaCha20-Poly1305. La password serve solo a derivare la chiave tramite scrypt, non viene salvata e non può essere recuperata da Risparmio Plus.</p>
            <p>Il file viene consegnato al foglio di condivisione del sistema operativo solo quando lo chiedi. Da quel momento potrà essere copiato o inviato secondo le tue scelte.</p>
          </PolicySection>
          <PolicySection id="diritti" eyebrow="05 / Il tuo controllo" title="Puoi portare via o cancellare tutto.">
            <p>Puoi esportare i tuoi dati, importarli su un altro dispositivo compatibile e usare “Elimina tutti i dati” per rimuovere dal database locale movimenti, budget, ricorrenze, categorie personalizzate e nome del profilo.</p>
            <p>Il blocco facoltativo con Face ID, Touch ID o codice del dispositivo viene gestito dal sistema operativo. Risparmio Plus non riceve né conserva dati biometrici.</p>
            <p>Il comportamento dei backup iCloud, Google One o del dispositivo dipende dalle impostazioni del sistema operativo e dalla configurazione finale dello store. L’export protetto ti permette di controllare direttamente una copia dei dati.</p>
          </PolicySection>
          <PolicySection id="contatti" eyebrow="06 / Parliamone" title="Hai una domanda?"><p>Per richieste relative alla privacy o alla gestione dei dati, scrivi a <a className="inline-link" href="mailto:it.musme@gmail.com">it.musme@gmail.com</a>.</p><p>Risparmio Plus è sviluppata da uno sviluppatore indipendente. Questa informativa descrive il comportamento tecnico dell’app e verrà aggiornata se aggiungeremo nuove funzioni, come sincronizzazione o servizi online.</p></PolicySection>
          <p className="policy-endnote"><Icon name="check" size={18} /> Questa pagina non incorpora strumenti di analytics o moduli di tracciamento.</p>
        </article>
      </main>
      <SiteFooter />
    </div>
  );
}

function SiteFooter() {
  return <footer className="site-footer"><div className="container footer-inner"><Logo /><div className="footer-meta"><span>Spese più chiare. Dati più tuoi.</span><a href="/privacy.html">Privacy policy</a><a href="mailto:it.musme@gmail.com">it.musme@gmail.com</a><span>© 2026 Risparmio Plus</span></div></div></footer>;
}

const isPrivacy = window.location.pathname.endsWith('/privacy') || window.location.pathname.endsWith('/privacy.html');
createRoot(document.getElementById('root')!).render(isPrivacy ? <PrivacyPage /> : <LandingPage />);
